import { createFileRoute } from "@tanstack/react-router";
import { WaitlistForm } from "@/components/kit-forms";
import { KIT_TAGS } from "@/lib/kit.functions";
import { Button } from "@/components/ui/button";
import { CEIC, CEIC_EVENT, CEIC_PRICING } from "@/lib/ceic";
import ogImageAsset from "@/assets/og-tb-logo-navy.png.asset.json";

const CEO_NETWORK = "https://ceopractitionernetwork.com";
const PAGE_URL = "https://tablesandbridges.com/conference";
const ogImage = `https://tablesandbridges.com${ogImageAsset.url}`;
const OG_IMAGE_ALT =
  "Tables and Bridges logo - a navy and green circular emblem of a table framed by a bridge arch, above the wordmark Tables and Bridges and the tagline: Building Trust. Connecting Communities. Creating Impact.";

const PAGE_TITLE = "CEIC 2027: Community Engagement Impact Conference | Tables and Bridges";
const PAGE_DESC =
  "CEIC 2027: From Events to Evidence. National virtual Community Engagement Impact Conference, March 30–31, 2027. Limited to 500 attendees. Registration not yet open.";

export const Route = createFileRoute("/conference")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "law enforcement community engagement conference, community policing conference, community engagement officer training, virtual national conference 2027",
      },
      { property: "og:site_name", content: "Tables and Bridges" },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:locale", content: "en_US" },
      { property: "og:image", content: ogImage },
      { property: "og:image:alt", content: OG_IMAGE_ALT },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: ogImage },
      { name: "twitter:image:alt", content: OG_IMAGE_ALT },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({ ...CEIC_EVENT, image: [ogImage] }),
      },
    ],
  }),
  component: ConferencePage,
});

const tracks = [
  { title: "Engagement Architecture", body: "Design engagement with purpose. Align the work with agency and community needs before an event begins." },
  { title: "Evidence Standard", body: "Move beyond activity counts. Identify meaningful evidence of impact, including trust indicators and behavior change." },
  { title: "Trust Infrastructure", body: "Build relationships, feedback loops, and institutional memory that last beyond a single event or officer." },
  { title: "Institutionalizing Community Engagement", body: "Embed engagement in leadership, policy, budget, and accountability so it becomes an agency-wide practice." },
];

function ConferencePage() {
  return (
    <>
      <section className="bg-navy text-warm-white">
        <div className="container-editorial pt-20 pb-16 md:pt-24 md:pb-20">
          <p className="eyebrow mb-6">{CEIC.edition} · National Virtual Conference</p>
          <h1 className="max-w-4xl font-display text-4xl md:text-6xl leading-tight tracking-normal">{CEIC.name}</h1>
          <p className="mt-6 text-lg md:text-xl font-semibold text-gold">{CEIC.dates} · {CEIC.format} · Limited to {CEIC.capacity} attendees</p>
          <span aria-hidden className="divider-gold mt-8" />
          <p className="mt-8 font-display text-3xl md:text-4xl italic text-gold">{CEIC.theme}</p>
          <p className="mt-6 max-w-3xl text-warm-white/85 text-base md:text-lg leading-relaxed">A shared learning journey for the professionals building community trust. Move from visible activity to purposeful engagement, meaningful evidence, and systems that carry the work forward.</p>
          <p className="mt-5 text-sm text-warm-white/80">Hosted by the CEO Practitioner Network. Powered by Tables and Bridges.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Button asChild className="btn-base btn-primary h-auto whitespace-normal text-center"><a href="#waitlist">Get Conference Updates</a></Button>
            <Button asChild variant="outline" className="btn-base btn-secondary-dark h-auto whitespace-normal text-center"><a href={CEIC.url} target="_blank" rel="noopener noreferrer">Visit the Official CEIC Site</a></Button>
          </div>
          <p className="mt-5 text-sm text-warm-white/85">Registration and payment are not yet open. Joining the waitlist does not purchase a ticket or reserve a seat.</p>
        </div>
      </section>

      <section className="bg-warm-white text-navy section-y">
        <div className="container-editorial">
          <p className="eyebrow mb-6">The Curriculum</p>
          <h2 className="font-display text-3xl md:text-4xl tracking-normal">Four tracks. One standard for impact.</h2>
          <ul className="mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {tracks.map((track, index) => (
              <li key={track.title} className="border-t border-navy/20 pt-6">
                <p className="text-sm font-semibold text-gold">0{index + 1}</p>
                <h3 className="mt-3 font-display text-2xl tracking-normal">{track.title}</h3>
                <p className="mt-4 text-navy/80 leading-relaxed">{track.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-deep-green text-warm-white section-y">
        <div className="container-editorial grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="eyebrow mb-6">Who It Is For</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-normal">For the people doing and advancing the work.</h2>
            <p className="mt-6 text-warm-white/85 leading-relaxed">Community Engagement officers, community relations and crime prevention professionals, supervisors, and agency leaders. Government employees, researchers, nonprofit professionals, and community partners invested in this field are also part of the conversation.</p>
          </div>
          <div>
            <p className="eyebrow mb-6">The Participant Experience</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-normal">More than passive viewing.</h2>
            <ul className="mt-6 space-y-4 text-warm-white/85 leading-relaxed">
              <li className="border-b border-warm-white/20 pb-4">Keynotes and facilitated small-group conversations at <strong>The Tables</strong>.</li>
              <li className="border-b border-warm-white/20 pb-4">Peer connection and networking through <strong>The Exchange</strong>.</li>
              <li>A certificate of completion and an initial action plan built around a real agency challenge.</li>
            </ul>
            <a href={`${CEIC.url}experience`} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex min-h-11 items-center text-gold underline underline-offset-4">Explore the CEIC experience</a>
          </div>
        </div>
      </section>

      <section className="bg-warm-white text-navy section-y">
        <div className="container-editorial">
          <p className="eyebrow mb-6">Plan Your Budget</p>
          <h2 className="font-display text-3xl md:text-4xl tracking-normal">Confirmed 2027 pricing.</h2>
          <p className="mt-6 max-w-2xl text-navy/80 leading-relaxed">These prices are provided for planning. Registration and payment are not yet open.</p>
          <dl className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {CEIC_PRICING.map((tier) => (
              <div key={tier.id} className="flex flex-col border border-navy/20 rounded-sm p-6">
                <dt className="min-h-12 text-sm font-semibold leading-relaxed">{tier.label}</dt>
                <dd className="mt-5 font-display text-4xl tracking-normal">${tier.price}</dd>
                <dd className="mt-5 text-sm text-navy/80 leading-relaxed">{tier.condition}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-8 flex flex-col sm:flex-row gap-x-8 gap-y-2">
            <a href={`${CEIC.url}pricing`} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center text-gold underline underline-offset-4">Official pricing details</a>
            <a href={CEO_NETWORK} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center text-gold underline underline-offset-4">Explore the CEO Practitioner Network</a>
          </div>
        </div>
      </section>

      <section id="waitlist" className="bg-navy text-warm-white section-y scroll-mt-24">
        <div className="container-editorial grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="eyebrow mb-6">Conference Updates</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-normal">Stay connected to CEIC 2027.</h2>
            <p className="mt-6 text-warm-white/85 leading-relaxed">Join the existing conference waitlist for program updates and notice when registration opens. This is an updates list, not a ticket purchase or a seat reservation. No payment is collected here.</p>
          </div>
          <div className="self-start border-l-2 border-gold pl-6 md:pl-8">
            <WaitlistForm tag={KIT_TAGS.conferenceWaitlist} sourcePage="/conference" ctaLabel="Join the Waitlist" placeholder="Your work email" variant="onGreen" successMessage="You are on the CEIC 2027 updates list. This is not a ticket purchase or seat reservation." />
            <p className="mt-6 text-sm text-warm-white/80 leading-relaxed">{CEIC.dates} · Virtual. Registration is not yet open.</p>
          </div>
        </div>
      </section>
    </>
  );
}
